/**
 * DataMapping Service API Client
 *
 * Calls the HIVE backend proxy at /v1/mapper/* which forwards
 * to the DataMapping sidecar service.
 */

import { getApiUrl } from "@/lib/api-config";
import { getAuthToken, getAuthTokenWithRefresh } from "@/lib/auth";

const API_URL = getApiUrl();

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  let token = getAuthToken();
  if (!token) token = await getAuthTokenWithRefresh();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const opts: RequestInit = {
    method,
    credentials: "include",
    headers: await authHeaders(),
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  if (signal) opts.signal = signal;
  const res = await fetch(`${API_URL}/v1${path}`, opts);
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`${res.status}: ${txt}`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MapperHealthResponse {
  status: string;
  connections: number;
  discovered: boolean;
  db?: string;
  cache?: string;
  mapper?: {
    semantic_enabled: boolean;
    semantic_index_size: number | null;
    semantic_index_error: string | null;
    semantic_ready: boolean;
  };
}

export interface MapperConnection {
  source_id: string;
  source_type: string;
  display_name: string;
  auth_type: string;
  endpoint_count: number;
  field_count: number;
}

export interface DiscoveryResult {
  total_nodes: number;
  total_edges: number;
  sources: number;
  cross_source_edges: number;
  identity_clusters: number;
  links: IdentityLink[];
  clusters: Array<{ entity_type: string; primary_key: string; fields: string[] }>;
  semantic_index_size?: number | null;
  semantic_index_error?: string | null;
  hive_space_id?: string | null;
  snapshot_version?: number | null;
}

export interface DiscoveryStatus {
  state: "idle" | "running" | "completed" | "error";
  started_at?: string;
  completed_at?: string;
  total_nodes?: number;
  total_edges?: number;
  cross_source_edges?: number;
  identity_clusters?: number;
  elapsed_ms?: number;
  error?: string;
  hive_space_id?: string | null;
  snapshot_version?: number | null;
}

export interface IdentityLink {
  from: string;
  to: string;
  type: string;
  confidence: number;
  confirmed?: boolean;
  rejected?: boolean;
}

export interface SpaceGraphSummary {
  hive_space_id: string | null;
  snapshot_version: number;
  connection_ids: string[];
  total_nodes: number;
  total_edges: number;
}

export interface FieldInventoryItem {
  uri: string;
  source_id: string;
  source_type: string;
  endpoint_id: string;
  endpoint_path: string;
  field_name: string;
  data_type: string;
  semantic_type?: string;
  description: string;
  sample_values?: unknown[];
  sample_stats?: Record<string, unknown> | null;
  field_metadata?: Record<string, unknown> | null;
  endpoint_metadata?: Record<string, unknown> | null;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface GraphNode {
  uri: string;
  field_name: string;
  source_id: string;
  data_type: string;
  semantic_type?: string;
  field_metadata?: Record<string, unknown>;
  endpoint_metadata?: Record<string, unknown>;
}

export interface GraphEdge {
  from: string;
  to: string;
  confidence: number;
  link_type: string;
}

export interface MatchedField {
  score: number;
  uri: string;
  source_id: string;
  field_name?: string;
  data_type: string;
  semantic_type?: string;
  description?: string;
  mapper_rationale?: string;
}

export interface CallStep {
  step_id: string;
  source_id: string;
  method: string;
  endpoint_path: string;
  fields_needed: string[];
  depends_on: string[];
}

export interface SchemaField {
  name: string;
  type: string;
  source: string;
}

export interface QueryResult {
  requirement: string;
  matched_fields: MatchedField[];
  call_plan: CallStep[];
  phases: string[][];
  join_keys: unknown[];
  channel_schema: SchemaField[];
  mapper_meta?: Record<string, unknown>;
}

export interface ExecuteResult {
  meta: {
    record_count?: number;
    total_latency_ms?: number;
    sources_called?: string[];
    mapper?: Record<string, unknown>;
  };
  channel_schema: SchemaField[];
  records: Array<{
    data: Record<string, unknown>;
    sources?: string[];
    join_confidence?: number;
  }>;
}


// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const fetchMapperHealth = () =>
  request<MapperHealthResponse>("GET", "/mapper/health");

export const fetchMapperConnections = () =>
  request<MapperConnection[]>("GET", "/mapper/connections");

export const runDiscovery = (opts?: {
  hive_space_id?: string;
  connection_ids?: string[];
}) =>
  request<{ status: string; hive_space_id?: string | null }>(
    "POST",
    "/mapper/discover",
    opts,
  );

export const fetchDiscoveryStatus = (hiveSpaceId?: string) =>
  request<DiscoveryStatus>(
    "GET",
    hiveSpaceId
      ? `/mapper/discover/status?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/discover/status",
  );

export const fetchDiscoveryLatest = (hiveSpaceId?: string) =>
  request<DiscoveryResult>(
    "GET",
    hiveSpaceId
      ? `/mapper/discover/latest?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/discover/latest",
  );

export const fetchFieldInventory = (hiveSpaceId?: string) =>
  request<FieldInventoryItem[]>(
    "GET",
    hiveSpaceId
      ? `/mapper/fields?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/fields",
  );

export const fetchGraph = (hiveSpaceId?: string) =>
  request<GraphData>(
    "GET",
    hiveSpaceId
      ? `/mapper/graph?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/graph",
  );

export const fetchGraphStats = (hiveSpaceId?: string) =>
  request<Record<string, unknown>>(
    "GET",
    hiveSpaceId
      ? `/mapper/graph/stats?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/graph/stats",
  );

export const runQuery = (
  requirement: string,
  hiveSpaceId?: string,
  signal?: AbortSignal,
) =>
  request<QueryResult>(
    "POST",
    "/mapper/query",
    {
      requirement,
      ...(hiveSpaceId ? { hive_space_id: hiveSpaceId } : {}),
    },
    signal,
  );

export const runExecute = (requirement: string, hiveSpaceId?: string) =>
  request<ExecuteResult>("POST", "/mapper/execute", {
    requirement,
    ...(hiveSpaceId ? { hive_space_id: hiveSpaceId } : {}),
  });

// Per-space graph lifecycle helpers — these resolve the space's connections
// backend-side and thread ``hive_space_id`` through every downstream call.
export const runSpaceDiscovery = (hiveSpaceId: string) =>
  request<{ status: string; hive_space_id: string; snapshot_version?: number }>(
    "POST",
    `/mapper/spaces/${encodeURIComponent(hiveSpaceId)}/discover`,
  );

export const fetchSpaceDiscoveryStatus = (hiveSpaceId: string) =>
  request<DiscoveryStatus>(
    "GET",
    `/mapper/spaces/${encodeURIComponent(hiveSpaceId)}/discover/status`,
  );

export const fetchSpaceDiscoveryLatest = (hiveSpaceId: string) =>
  request<DiscoveryResult>(
    "GET",
    `/mapper/spaces/${encodeURIComponent(hiveSpaceId)}/discover/latest`,
  );

export const fetchSpaceGraph = (hiveSpaceId: string) =>
  request<GraphData>(
    "GET",
    `/mapper/spaces/${encodeURIComponent(hiveSpaceId)}/graph`,
  );

export const listSpaceGraphs = () =>
  request<{ spaces: SpaceGraphSummary[] }>("GET", "/mapper/spaces");

// ---------------------------------------------------------------------------
// Link decisions (Phase 3 confirm/reject)
// ---------------------------------------------------------------------------

export interface LinkRecord {
  id: number;
  from: string;
  to: string;
  type: string;
  confidence: number;
  confirmed: boolean;
  rejected: boolean;
  evidence: Record<string, unknown>;
}

export interface LinkListResponse {
  hive_space_id: string | null;
  snapshot_version?: number;
  links: LinkRecord[];
}

export const fetchLinks = (opts?: { hiveSpaceId?: string; onlyUnresolved?: boolean }) => {
  const params = new URLSearchParams();
  if (opts?.hiveSpaceId) params.set("hive_space_id", opts.hiveSpaceId);
  if (opts?.onlyUnresolved) params.set("only_unresolved", "true");
  const qs = params.toString();
  return request<LinkListResponse>("GET", `/mapper/links${qs ? `?${qs}` : ""}`);
};

export const decideLink = (linkId: number, decision: "confirm" | "reject") =>
  request<{
    id: number;
    confirmed: boolean;
    rejected: boolean;
    from: string;
    to: string;
    link_type: string;
    confidence: number;
  }>("POST", `/mapper/links/${linkId}/decision`, { decision });

// ---------------------------------------------------------------------------
// Canonical entities (Phase 4)
// ---------------------------------------------------------------------------

export interface CanonicalFieldDef {
  name: string;
  aliases: string[];
  semantic_type: string | null;
  required: boolean;
}

export interface CanonicalEntityBinding {
  endpoint_count: number;
  source_count: number;
  origin: "builtin" | "discovered";
  kind?: string;
  confidence?: number;
  sample_name?: string | null;
  endpoint_ids?: string[];
  field_count?: number;
}

export interface CanonicalEntityDef {
  id: string;
  display_name: string;
  description: string;
  field_count?: number;
  omitted_field_count?: number;
  fields?: CanonicalFieldDef[];
  signal_fields?: string[];
  path_keywords?: string[];
  binding?: CanonicalEntityBinding;
  binding_detail?: SpaceEntityBindingView;
}

export interface SpaceEntityBindingView {
  entity_id: string;
  confidence: number;
  endpoint_ids: string[];
  field_bindings: Record<string, string[]>;
  rationale: string;
}

export interface ResolvedEntityReference {
  reference: string;
  entity: string;
  field: string;
  field_uris: string[];
  endpoint_ids: string[];
  confidence: number;
  rationale: string;
}

export const fetchEntityCatalog = (
  hiveSpaceId?: string | null,
  opts?: { view?: "full" | "summary" },
) => {
  const view = opts?.view ?? (hiveSpaceId ? "summary" : "full");
  const path = hiveSpaceId
    ? `/mapper/entities/catalog?hive_space_id=${encodeURIComponent(hiveSpaceId)}&view=${view}`
    : "/mapper/entities/catalog";
  return request<{
    hive_space_id: string | null;
    snapshot_version: number | null;
    entities: CanonicalEntityDef[];
  }>("GET", path);
};

export const fetchEntityCatalogDetail = (
  hiveSpaceId: string,
  entityId: string,
  opts?: { fieldLimit?: number },
) => {
  const fieldLimit = opts?.fieldLimit ?? 80;
  return request<{
    hive_space_id: string | null;
    snapshot_version: number | null;
    entity: CanonicalEntityDef;
  }>(
    "GET",
    `/mapper/entities/catalog/${encodeURIComponent(entityId)}?hive_space_id=${encodeURIComponent(hiveSpaceId)}&field_limit=${fieldLimit}`,
  );
};

export const fetchSpaceEntities = (
  spaceId: string,
  opts?: { snapshotVersion?: number },
) => {
  const qs = opts?.snapshotVersion ? `?snapshot_version=${opts.snapshotVersion}` : "";
  return request<{
    hive_space_id: string | null;
    snapshot_version: number | null;
    entities: SpaceEntityBindingView[];
  }>("GET", `/mapper/spaces/${encodeURIComponent(spaceId)}/entities${qs}`);
};

export const resolveSpaceEntities = (spaceId: string, references: string[]) =>
  request<{
    hive_space_id: string | null;
    snapshot_version: number | null;
    resolved: ResolvedEntityReference[];
    unresolved: string[];
  }>(
    "POST",
    `/mapper/spaces/${encodeURIComponent(spaceId)}/entities/resolve`,
    { references },
  );

// ---------------------------------------------------------------------------
// Sync HIVE connections into DataMapping sidecar
// ---------------------------------------------------------------------------

export const syncHiveConnections = (hiveSpaceId?: string) =>
  request<{
    synced: number;
    total: number;
    doc_sources?: number;
    doc_source_ids?: string[];
    introspected?: Record<string, string>;
  }>(
    "POST",
    hiveSpaceId
      ? `/mapper/sync-connections?hive_space_id=${encodeURIComponent(hiveSpaceId)}`
      : "/mapper/sync-connections",
  );

// ---------------------------------------------------------------------------
// Endpoint rankings — proactive cache of endpoint quality + data samples
// ---------------------------------------------------------------------------
//
// Populated by the backend ranking pipeline on every preflight / channel
// refresh.  The data-explorer and motif install flows read from here to
// surface winning endpoint, confidence band, score breakdown, and real
// record samples — without firing a new upstream API call.

export interface EndpointRankingBreakdown {
  semantic_match?: number;
  schema_fit?: number;
  data_quality?: number;
  relationship?: number;
  freshness?: number;
}

export interface CachedRankedEndpoint {
  source_id: string;
  endpoint_path: string;
  method: string;
  total_score: number;
  confidence_band: "strong" | "moderate" | "weak" | "poor" | string;
  rank_position: number;
  selected: boolean;
  breakdown: EndpointRankingBreakdown;
  snapshot_version: number | null;
  llm_tiebreak_used: boolean;
  evaluated_at: string;
  sample_records: Array<Record<string, unknown>>;
  sample_size: number;
  field_profile: Record<string, unknown>;
}

export interface CachedRanking {
  collection_name: string;
  hive_space_id: string | null;
  evaluation_phase: string;
  evaluated_at: string;
  snapshot_version: number | null;
  winner: CachedRankedEndpoint | null;
  alternatives: CachedRankedEndpoint[];
  is_trusted: boolean;
}

export interface EndpointRankingsResponse {
  hive_space_id: string | null;
  collections: Record<string, CachedRanking>;
  total_collections: number;
  trusted_winners: number;
}

// ---------------------------------------------------------------------------
// Endpoint inventory — pre-ranked quality index for all endpoints
// ---------------------------------------------------------------------------

export interface InventoryRun {
  id: string;
  status: string;
  total_endpoints: number;
  sampled_endpoints: number;
  started_at: string;
  completed_at: string;
}

export interface InventoryScore {
  connection_id: string;
  endpoint_path: string;
  method: string;
  source_id: string;
  total_score: number;
  confidence_band: string;
  data_quality_score: number;
  schema_richness_score: number;
  response_health_score: number;
  sample_size: number;
  field_count: number;
  field_names: string[];
  sample_records: Array<Record<string, unknown>>;
  field_profile: Record<string, unknown>;
}

export const triggerInventorySweep = (opts?: {
  hiveSpaceId?: string;
  background?: boolean;
}) => {
  const params = new URLSearchParams();
  if (opts?.hiveSpaceId) params.set("hive_space_id", opts.hiveSpaceId);
  if (opts?.background !== undefined)
    params.set("background", String(opts.background));
  const qs = params.toString();
  return request<{ run_id: string; status: string }>(
    "POST",
    `/data-spaces/endpoint-inventory/sweep${qs ? `?${qs}` : ""}`,
  );
};

export const fetchLatestInventory = (opts?: {
  hiveSpaceId?: string;
  minScore?: number;
  limit?: number;
  signal?: AbortSignal;
}) => {
  const params = new URLSearchParams();
  if (opts?.hiveSpaceId) params.set("hive_space_id", opts.hiveSpaceId);
  if (opts?.minScore !== undefined)
    params.set("min_score", String(opts.minScore));
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return request<{
    run: InventoryRun | null;
    scores: InventoryScore[];
    total_scores: number;
  }>(
    "GET",
    `/data-spaces/endpoint-inventory/latest${qs ? `?${qs}` : ""}`,
    undefined,
    opts?.signal,
  );
};

export const fetchBestEndpointsForEntity = (
  entity: string,
  opts?: { hiveSpaceId?: string; minScore?: number; limit?: number },
) => {
  const params = new URLSearchParams();
  params.set("entity", entity);
  if (opts?.hiveSpaceId) params.set("hive_space_id", opts.hiveSpaceId);
  if (opts?.minScore !== undefined)
    params.set("min_score", String(opts.minScore));
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  return request<{
    entity: string;
    endpoints: InventoryScore[];
    total: number;
  }>("GET", `/data-spaces/endpoint-inventory/best-for-entity?${params.toString()}`);
};

export const fetchEndpointRankings = (opts?: {
  hiveSpaceId?: string;
  collection?: string;
  maxAgeSeconds?: number;
  signal?: AbortSignal;
}) => {
  const params = new URLSearchParams();
  if (opts?.hiveSpaceId) params.set("hive_space_id", opts.hiveSpaceId);
  if (opts?.collection) params.set("collection", opts.collection);
  if (opts?.maxAgeSeconds !== undefined)
    params.set("max_age_seconds", String(opts.maxAgeSeconds));
  const qs = params.toString();
  return request<EndpointRankingsResponse>(
    "GET",
    `/data-spaces/endpoint-rankings${qs ? `?${qs}` : ""}`,
    undefined,
    opts?.signal,
  );
};

// ---------------------------------------------------------------------------
// Color utilities for source-based theming
// ---------------------------------------------------------------------------

const SRC_HUES: Record<string, string> = {};
const PALETTE = [
  "#635BFF", "#FF5C35", "#336791", "#2EA043", "#58A6FF",
  "#EF9F27", "#BC8CFF", "#F0883E", "#DA3633", "#3FB950",
];

export function srcColor(sourceId: string): string {
  if (!SRC_HUES[sourceId]) {
    const idx = Object.keys(SRC_HUES).length % PALETTE.length;
    SRC_HUES[sourceId] = PALETTE[idx];
  }
  return SRC_HUES[sourceId];
}

export const LINK_COLORS: Record<string, string> = {
  declared_fk: "#58A6FF",
  foreign_key: "#58A6FF",
  value_overlap: "#F0883E",
  exact_match: "#F0883E",
  format_match: "#F0883E",
  name_similarity: "#34D399",
  lexical_match: "#34D399",
  neural_attention: "#BC8CFF",
  semantic_match: "#BC8CFF",
  embedding_match: "#BC8CFF",
  temporal: "#BC8CFF",
  structural: "#BC8CFF",
};
